import { FC } from 'react';
import { cx } from '@emotion/css';
import { useTranslation } from 'next-i18next';
import { DSIcon, DSLinkButton, DSText } from '@stihl-design-system/components';

import { link } from '@phoenix/core/frontend/shared/util/navigation/link.utils';
import { formatDateStr } from '@phoenix/core/frontend/shared/util/format/dates';
import { useBreakpoints } from '@phoenix/design-system/frontend/ui/breakpoints/use-breakpoints';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { containerQueries } from '@phoenix/design-system/frontend/ui/breakpoints/breakpoints';
import { Image } from '@phoenix/team-m/dashboard/frontend/shared/components/image';

import { Raffle } from '@phoenix/team-m/my-tool-shed/frontend/domain/model/benefits.model';

export const RaffleCard: FC<Raffle> = ({
  id,
  images,
  eligible,
  productRegistrationRequired,
  startDateOfPurchaseDate,
  endDateOfPurchaseDate,
}) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const showDisclaimer = productRegistrationRequired && !eligible;
  const hasPeriodDates = startDateOfPurchaseDate && endDateOfPurchaseDate;

  // Raffle is eligible without product registration requirement
  let title = 'dashboard_bannerRaffle_header';
  let description = 'dashboard_bannerRaffleEveryoneParticipate_description';
  let linkButton = { text: 'dashboard_bannerRaffleParticipate_button', url: `tool-shed/my-benefits?raffleId=${id}` };

  // Raffle is not eligible due to product registration requirement
  if (showDisclaimer) {
    description = 'dashboard_bannerRaffle_description';
    linkButton = { text: 'dashboard_tool_overview_button_addTool', url: 'tool-shed/add-tool' };
  }

  // Raffle is eligible after product registration requirement
  if (productRegistrationRequired && eligible) {
    title = 'dashboard_bannerRaffleParticipate_header';
    description = 'dashboard_bannerRaffleParticipate_description';
  }

  return (
    <div
      className="flex h-[328px] tablet:h-[496px] flex-col items-center justify-center rounded-md z-10 bg-secondary-light"
      data-testid="raffle-card"
    >
      <div className="relative h-[260px] w-full rounded-t-md overflow-hidden">
        <Image
          src={images.secondary?.url ?? ''}
          alt=""
          aria-hidden
          sizes={`(max-width: ${containerQueries.miniTablet}) 100vw, 25vw`}
          className="object-cover"
          fill
        />
      </div>
      <div className={cx(showDisclaimer ? 'tablet:pb-4' : 'tablet:pb-8', 'relative text-center ml-5 mr-5 pt-6 tablet:pt-10 pb-4')}>
        <div className="absolute -top-5 left-0 right-0 m-auto w-10 h-10 tablet:w-12 tablet:h-12 flex items-center justify-center bg-white rounded-full border-2 border-black">
          <DSIcon source={getDesignSystemIcon('icon-present.svg')} size={tablet ? 'x-large' : 'large'} />
        </div>
        <div className="m-auto max-w-[368px]">
          <p className="stihl-text-h4-desktop">{t(title)}</p>
          <DSText className={cx(showDisclaimer && 'hidden tablet:block', 'mt-1.5')}>{t(description)}</DSText>
        </div>
        <DSLinkButton href={link(linkButton.url)} className="mt-4 tablet:mt-5">
          {t(linkButton.text)}
        </DSLinkButton>
        {showDisclaimer && (
          <DSText className="mt-3 tablet:mt-4" size="x-small">
            {hasPeriodDates &&
              t('dashboard_bannerRaffle_disclaimer', {
                periodStart: formatDateStr(startDateOfPurchaseDate),
                periodEnd: formatDateStr(endDateOfPurchaseDate),
              })}
          </DSText>
        )}
      </div>
    </div>
  );
};
